import { rewriteText } from "../../redux/actions/designActions";
import { createRewriteTitlePrompt } from "../../utils/utilityFunctions";
import { createRandomId } from "../TemplateBuilder/Elements/StandardElementProperties";
import logoPlaceholder from "../../assets/img/logo-placeholder.png";

export function resetPostButtonIndicators(post) {
  let newPost = { ...post };

  newPost.isDownloading = false;
  newPost.isDownloaded = false;
  newPost.isScheduling = false;
  newPost.isScheduled = false;
  newPost.error = "";
  newPost.warning = null;

  return newPost;
}

export function setPostColorIndex(
  myPosts,
  designColors,
  selectedColorIndex,
  useAllColors
) {
  // Set the post color
  myPosts.forEach((post) => {
    // Skip locked posts
    if (post.isLocked) return;

    if (useAllColors !== undefined && useAllColors) {
      post.colorIndex = Math.floor(Math.random() * designColors.length);
      post.color = designColors[post.colorIndex].color;
    } else {
      post.colorIndex = selectedColorIndex;
      post.color = designColors[selectedColorIndex].color;
    }

    // reset post button indicators
    post = resetPostButtonIndicators(post);
  });

  return myPosts;
}

export function setTemplateType(
  myPosts,
  templateSelections,
  isRandom,
  standardUserAndSelectedTemplates,
  useOneOfEachTemplate,
  startIndex = 0
) {
  // Early return if no templates are available (e.g., still loading)
  if (
    !standardUserAndSelectedTemplates ||
    standardUserAndSelectedTemplates.length === 0
  ) {
    console.warn(
      "No templates available for setTemplateType. Templates may still be loading."
    );
    return myPosts; // Return posts unchanged
  }

  // Convert allTemplates into a map for faster lookup
  const templateMap = new Map();
  standardUserAndSelectedTemplates.forEach((template) => {
    templateMap.set(template.templateId, template);
  });

  return myPosts.map((post, index) => {
    // Skip locked posts
    if (post.isLocked) return post;

    let randomTemplate;
    if (isRandom) {
      // Get a random template
      randomTemplate =
        standardUserAndSelectedTemplates[
          Math.floor(Math.random() * standardUserAndSelectedTemplates.length)
        ];
    } else if (templateSelections.length == 0) {
      // When no templates are selected, use standard templates in order
      if (useOneOfEachTemplate) {
        // Cycle through all available templates in order
        const templateIndex =
          (startIndex + index) % standardUserAndSelectedTemplates.length;
        randomTemplate = standardUserAndSelectedTemplates[templateIndex];
      } else {
        // Get a random template from all available templates
        randomTemplate =
          standardUserAndSelectedTemplates[
            Math.floor(Math.random() * standardUserAndSelectedTemplates.length)
          ];
      }
    } else {
      let randomTemplateId;

      // Use one of each template
      if (useOneOfEachTemplate) {
        randomTemplateId =
          templateSelections[(startIndex + index) % templateSelections.length];
      } else {
        // Get a random template from the selected templates
        randomTemplateId =
          templateSelections[
            Math.floor(Math.random() * templateSelections.length)
          ];
      }

      // Use the map for faster lookup
      randomTemplate = templateMap.get(randomTemplateId);

      if (!randomTemplate) {
        console.log("Random template not found");

        // Remove the randomTemplateId from the templateSelections
        // This template Id is most likely a custom template that has been deleted
        templateSelections = templateSelections.filter(
          (templateId) => templateId !== randomTemplateId
        );

        if (templateSelections.length != 0) {
          // get a random template from the selected templates
          randomTemplateId =
            templateSelections[
              Math.floor(Math.random() * templateSelections.length)
            ];

          randomTemplate = templateMap.get(randomTemplateId);
        } else {
          // If there are no more templates to select, then select a random template from all templates
          randomTemplate =
            standardUserAndSelectedTemplates[
              Math.floor(
                Math.random() * standardUserAndSelectedTemplates.length
              )
            ];
          randomTemplateId = randomTemplate.templateId;
        }
      }
    }

    // Final check to see if randomTemplate is still undefined
    if (!randomTemplate) {
      // Get a random template from templateMap
      randomTemplate = templateMap.get(
        Array.from(templateMap.keys())[
          Math.floor(Math.random() * templateMap.size)
        ]
      );
    }

    // Make a deep copy of randomTemplate
    if (randomTemplate) {
      post.template = JSON.parse(JSON.stringify(randomTemplate));
      post.templateType = randomTemplate.templateId;
    } else {
      console.warn(
        "randomTemplate is undefined. randomTemplateId: ",
        typeof randomTemplateId !== "undefined" ? randomTemplateId : "undefined"
      );
      console.warn("templateSelections: ", templateSelections);
      console.warn(
        "standardUserAndSelectedTemplates: ",
        standardUserAndSelectedTemplates
      );
      // Set a fallback empty template to prevent crashes
      post.template = { layers: [] };
      post.templateType = "";
    }

    // reset post button indicators
    post = resetPostButtonIndicators(post);

    return post;
  });
}

// Handle image randomisation
export function selectPostImages(
  posts,
  isRandomImages,
  useAllDetectedImages,
  selectedDetectedImages,
  selectedUploadedImages,
  forceRandomImages,
  logoImage,
  useOneOfEachImage
) {
  // Handle use one of each
  if (
    useOneOfEachImage == true &&
    isRandomImages == false &&
    forceRandomImages == false
  ) {
    return posts.map((post, postIndex) => {
      // Skip locked posts
      if (post.isLocked) return post;

      // Determine image options
      let allImageOptions = [
        ...selectedUploadedImages,
        ...(isRandomImages || forceRandomImages || useAllDetectedImages
          ? post.allImages
          : selectedDetectedImages),
      ];

      // Enhance with optimal/refined images
      allImageOptions = getOptimalImageOptions(post, allImageOptions);

      // Set the image options if it is only using one of each image
      let newTemplateLayers = setLayerImagesWithOneOfEachImage(
        post.template.layers,
        allImageOptions,
        logoImage,
        postIndex
      );

      // Deep copy of layers
      post.template.layers = [...newTemplateLayers];

      // reset post button indicators
      return resetPostButtonIndicators(post);
    });
  }
  // Handle randomizing images
  else if (
    isRandomImages ||
    forceRandomImages ||
    useAllDetectedImages ||
    (useAllDetectedImages == false &&
      selectedDetectedImages.concat(selectedUploadedImages).length > 0)
  ) {
    return posts.map((post) => {
      // Skip locked posts
      if (post.isLocked) return post;

      // Determine image options
      let allImageOptions = [
        ...(isRandomImages || forceRandomImages || useAllDetectedImages
          ? post.allImages
          : selectedDetectedImages),
        ...selectedUploadedImages,
      ];

      // Enhance with optimal/refined images
      allImageOptions = getOptimalImageOptions(post, allImageOptions);

      let newTemplateLayers = setLayerImagesRandomly(
        post.template.layers,
        allImageOptions,
        logoImage
      );

      // Deep copy of layers
      post.template.layers = [...newTemplateLayers];

      // reset post button indicators
      return resetPostButtonIndicators(post);
    });
  } else {
    // Handle a base case where no images are selected and there are no special settings
    return posts.map((post) => {
      // Skip locked posts
      if (post.isLocked) return post;

      // Determine image options
      let allImageOptions = [
        ...post.allImages,
        ...selectedDetectedImages,
        ...selectedUploadedImages,
      ];

      // Enhance with optimal/refined images
      allImageOptions = getOptimalImageOptions(post, allImageOptions);

      let newTemplateLayers = setLayerImagesRandomly(
        post.template.layers,
        allImageOptions,
        logoImage
      );

      // Deep copy of layers
      post.template.layers = [...newTemplateLayers];

      // reset post button indicators
      return resetPostButtonIndicators(post);
    });
  }
}

/**
 * Creates a refined image pool by prioritizing optimalSelectedImage and refinedImages
 * @param {Object} post - Post object with optimalSelectedImage and refinedImages
 * @param {Array} originalImages - The original array of images to filter/enhance
 * @returns {Array} Enhanced image array with quality images prioritized
 */
function getOptimalImageOptions(post, originalImages) {
  if (!originalImages || originalImages.length === 0) {
    return [];
  }

  let enhancedImages = [...originalImages];

  // If we have optimal or refined images, create a better pool
  if (
    post.optimalSelectedImage?.url ||
    (post.refinedImages && post.refinedImages.length > 0)
  ) {
    let qualityImages = [];

    // Add optimal image if available
    if (post.optimalSelectedImage?.url) {
      qualityImages.push(post.optimalSelectedImage.url);
    }

    // Add refined images if available
    if (post.refinedImages && post.refinedImages.length > 0) {
      qualityImages.push(...post.refinedImages);
    }

    // Remove duplicates from quality images
    qualityImages = [...new Set(qualityImages)];

    // Filter quality images to only include those that exist in originalImages
    const availableQualityImages = qualityImages.filter((img) =>
      originalImages.includes(img)
    );

    if (availableQualityImages.length > 0) {
      // Use quality images, but fill up to original length with remaining original images
      const remainingOriginal = originalImages.filter(
        (img) => !availableQualityImages.includes(img)
      );
      enhancedImages = [...availableQualityImages, ...remainingOriginal];
    }
  }

  return enhancedImages;
}

function setLayerImagesRandomly(layers, imageOptions, logoImage) {
  let newLayers = [...layers];
  let unusedImages = [...imageOptions];

  newLayers.forEach((layer, layerIndex) => {
    // If the layer doesnt use detected image or useLogoImage then skip this
    if (!layer.useDetectedImages && !layer.useLogoImage) return;

    if (
      layer.type === "Image" ||
      (layer.type === "SVG" && layer.containsImage)
    ) {
      let imageURLToUse = layer.type === "SVG" ? layer.imageSrc : layer.src;

      // Find image to use
      // Handle logo image
      if (layer.useLogoImage && layer.useLogoImage == true) {
        imageURLToUse = logoImage ? logoImage : logoPlaceholder;
      } else if (layer.useDetectedImages == true && imageOptions.length > 0) {
        // Use the images at random but don't repeat the same image

        // Refill the unusedImages array if it's empty
        if (unusedImages.length === 0) {
          unusedImages = [...imageOptions];
        }

        let randomIndex = Math.floor(Math.random() * unusedImages.length);
        imageURLToUse = unusedImages[randomIndex];

        // Remove the used image from the array
        unusedImages.splice(randomIndex, 1);
      }

      // Set the image
      if (layer.type === "Image") {
        newLayers[layerIndex].src = imageURLToUse;
      } else if (layer.type === "SVG") {
        newLayers[layerIndex].imageSrc = imageURLToUse;
      }
    }
  });

  return newLayers;
}

function setLayerImagesWithOneOfEachImage(
  layers,
  imageOptions,
  logoImage,
  postIndex
) {
  let newLayers = [...layers];
  let imageIndex = postIndex;

  newLayers.forEach((layer, layerIndex) => {
    // If the layer doesnt use detected image or useLogoImage then skip this
    if (!layer.useDetectedImages && !layer.useLogoImage) return;

    if (
      layer.type === "Image" ||
      (layer.type === "SVG" && layer.containsImage)
    ) {
      let imageURLToUse = layer.type === "SVG" ? layer.imageSrc : layer.src;

      // Handle logo image
      if (layer.useLogoImage && layer.useLogoImage == true) {
        imageURLToUse = logoImage ? logoImage : logoPlaceholder;
      } else if (layer.useDetectedImages == true && imageOptions.length > 0) {
        // Handle use one of each image
        imageURLToUse = imageOptions[imageIndex % imageOptions.length];
        imageIndex++;
      }

      // Set the image
      if (layer.type === "Image") {
        newLayers[layerIndex].src = imageURLToUse;
      } else if (layer.type === "SVG") {
        newLayers[layerIndex].imageSrc = imageURLToUse;
      }
    }
  });

  return newLayers;
}

export async function shuffleDemoImage(
  temporaryPost,
  setLoading,
  allTemplates,
  logoImage
) {
  setLoading(true);

  let newTitle = temporaryPost.title;

  if (temporaryPost.rewriteTitle) {
    let prompt = createRewriteTitlePrompt(
      temporaryPost.title,
      temporaryPost.designSettings.aiLanguage
        ? temporaryPost.designSettings.aiLanguage
        : "English",
      temporaryPost.designSettings.aiKeywords
        ? temporaryPost.designSettings.aiKeywords
        : "",
      temporaryPost.designSettings.aiTone
        ? temporaryPost.designSettings.aiTone
        : ""
    );

    let newTextChoices = await rewriteText(prompt, 1);
    newTitle = newTextChoices[0];
  }

  let postsWithColor = setPostColorIndex(
    [{ ...temporaryPost, title: newTitle }],
    temporaryPost.designSettings.designColors,
    temporaryPost.designSettings.selectedColorIndex,
    temporaryPost.designSettings.useAllColors
  );

  let postsWithTemplateType = setTemplateType(
    postsWithColor,
    temporaryPost.designSettings.templateSelections,
    temporaryPost.designSettings.isRandomTemplates,
    allTemplates,
    false
  );

  let postsWithRandomImage = selectPostImages(
    postsWithTemplateType,
    temporaryPost.designSettings.isRandomImages,
    temporaryPost.designSettings.useAllDetectedImages,
    temporaryPost.designSettings.selectedDetectedImages,
    temporaryPost.designSettings.selectedUploadedImages,
    false,
    logoImage,
    false // use random images
  );

  setLoading(false);
  return postsWithRandomImage[0];
}

export function addBlankPosts(
  numberOfPostsToGenerate,
  designColors,
  selectedColorIndex,
  useAllColors,
  templateSelections,
  isRandomTemplates,
  userTemplates,
  standardTemplates,
  useOneOfEachTemplate,
  isRandomImages,
  useAllDetectedImages,
  selectedDetectedImages,
  selectedUploadedImages,
  logoImage,
  useOneOfEachImage,
  addPosts,
  currentPostCount = 0
) {
  let allPosts = [];
  for (let i = 0; i < numberOfPostsToGenerate; i++) {
    let blankPost = getBlankPost();

    allPosts.push(blankPost);
  }

  let postsWithColor = setPostColorIndex(
    allPosts,
    designColors,
    selectedColorIndex,
    useAllColors
  );

  let postsWithTemplateType = setTemplateType(
    postsWithColor,
    templateSelections,
    isRandomTemplates,
    [...userTemplates, ...standardTemplates],
    useOneOfEachTemplate,
    currentPostCount
  );

  let postsWithSetImages = selectPostImages(
    postsWithTemplateType,
    isRandomImages,
    useAllDetectedImages,
    selectedDetectedImages,
    selectedUploadedImages,
    false,
    logoImage,
    useOneOfEachImage
  );

  addPosts(postsWithSetImages);
}

export function replaceWithBlankPosts(
  numberOfPostsToGenerate,
  designColors,
  selectedColorIndex,
  useAllColors,
  templateSelections,
  isRandomTemplates,
  userTemplates,
  standardTemplates,
  useOneOfEachTemplate,
  isRandomImages,
  useAllDetectedImages,
  selectedDetectedImages,
  selectedUploadedImages,
  logoImage,
  useOneOfEachImage,
  setPosts
) {
  let allPosts = [];
  for (let i = 0; i < numberOfPostsToGenerate; i++) {
    let blankPost = getBlankPost();

    allPosts.push(blankPost);
  }

  let postsWithColor = setPostColorIndex(
    allPosts,
    designColors,
    selectedColorIndex,
    useAllColors
  );

  let postsWithTemplateType = setTemplateType(
    postsWithColor,
    templateSelections,
    isRandomTemplates,
    [...userTemplates, ...standardTemplates],
    useOneOfEachTemplate
  );

  let postsWithSetImages = selectPostImages(
    postsWithTemplateType,
    isRandomImages,
    useAllDetectedImages,
    selectedDetectedImages,
    selectedUploadedImages,
    false,
    logoImage,
    useOneOfEachImage
  );

  setPosts(postsWithSetImages);
}

export function getBlankPost() {
  return {
    templateType: "",
    id: createRandomId(),
    colorIndex: 1,
    color: {
      r: 81,
      g: 195,
      b: 166,
      a: 1,
    },
    title: "",
    date: "",
    description: "",
    altText: "",
    allImages: [],
    url: "",
    URLText: "",
    isScheduling: false,
    isScheduled: false,
    isDownloading: false,
    isDownloaded: false,
    isPosting: false,
    isPosted: false,
    error: "",
    selectedBoards: [],
  };
}
